import { useCallback, useMemo, useState } from 'react';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useBulkUpdateMaterialRemarks, useManuallyCompleteMaterials } from '../../api/vegApl';
import { useConfirm } from '../../contexts/confirmDialogContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useGlobalState } from '../../contexts/GlobalState';
import { buildMaterialCompletionDescription } from '../common/bulkActionMessages';
import type { TrialMaterial } from './trailInterface';

type UseTrialMaterialBulkActionsParams = {
    trialGridData: TrialMaterial[];
    fetchTrials: () => void;
};

export const useTrialMaterialBulkActions = ({
    trialGridData,
    fetchTrials,
}: UseTrialMaterialBulkActionsParams) => {
    const { confirm } = useConfirm();
    const { showSnackbar } = useSnackbar();
    const { state: { user_id_db } } = useGlobalState();

    const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
    const [openRemarkDialog, setOpenRemarkDialog] = useState(false);
    const [selectedBulkRemark, setSelectedBulkRemark] = useState<string>('');
    const [bulkRemarkError, setBulkRemarkError] = useState('');
    const [isBulkRemarkSubmitting, setIsBulkRemarkSubmitting] = useState(false);
    const [isManualCompleteSubmitting, setIsManualCompleteSubmitting] = useState(false);

    const selectedMaterialRows = useMemo(() => {
        const selectedIds = new Set((selectionModel ?? []).map((id) => String(id)));
        return trialGridData.filter((row: any) => row?._rowType === 'parent' && selectedIds.has(String(row?._rowUid)));
    }, [selectionModel, trialGridData]);

    const selectedMaterialIds = useMemo(() => {
        return selectedMaterialRows
            .map((row: any) => String(row?.mat_id ?? '').trim())
            .filter(Boolean);
    }, [selectedMaterialRows]);

    const canMarkSelectedAsComplete = useMemo(() => {
        const normalize = (value: any) => String(value ?? '').trim().toLowerCase();
        if (selectedMaterialRows.length === 0) return false;
        return selectedMaterialRows.every((row: any) => normalize(row?.material_status) === 'ready for harvest');
    }, [selectedMaterialRows]);

    const clearBulkSelection = useCallback(() => {
        setSelectionModel([]);
    }, []);

    const { mutate: bulkUpdateRemarks } = useBulkUpdateMaterialRemarks({
        mutation: {
            onSuccess: (response: any) => {
                setIsBulkRemarkSubmitting(false);
                setOpenRemarkDialog(false);
                setSelectedBulkRemark('');
                const message = response?.data?.message || response?.data?.detail || 'Material remarks updated successfully';
                showSnackbar({ message, severity: 'success' });
                clearBulkSelection();
                fetchTrials();
            },
            onError: (error: any) => {
                setIsBulkRemarkSubmitting(false);
                const detail = error?.response?.data?.detail;
                showSnackbar({ message: typeof detail === 'string' ? detail : 'Failed to update remarks', severity: 'error' });
            }
        }
    });

    const { mutate: manuallyCompleteMaterials } = useManuallyCompleteMaterials({
        mutation: {
            onSuccess: (response: any) => {
                setIsManualCompleteSubmitting(false);
                const message = response?.data?.message || response?.data?.detail || 'Selected materials marked as completed';
                showSnackbar({ message, severity: 'success' });
                clearBulkSelection();
                fetchTrials();
            },
            onError: (error: any) => {
                setIsManualCompleteSubmitting(false);
                const detail = error?.response?.data?.detail;
                showSnackbar({ message: typeof detail === 'string' ? detail : 'Failed to mark materials as completed', severity: 'error' });
            }
        }
    });

    const handleOpenUpdateRemark = useCallback(() => {
        setBulkRemarkError('');
        setOpenRemarkDialog(true);
    }, []);

    const handleSaveBulkRemark = useCallback(() => {
        if (!selectedBulkRemark) {
            setBulkRemarkError('Please select a remark');
            return;
        }
        if (!user_id_db) {
            showSnackbar({ message: 'Unable to identify current user', severity: 'error' });
            return;
        }
        if (selectedMaterialIds.length === 0) {
            showSnackbar({ message: 'Please select at least one material', severity: 'warning' });
            return;
        }

        setBulkRemarkError('');
        setIsBulkRemarkSubmitting(true);
        bulkUpdateRemarks({
            data: {
                material_ids: selectedMaterialIds,
                remark_id: Number(selectedBulkRemark),
                user_id: Number(user_id_db),
            }
        });
    }, [selectedBulkRemark, user_id_db, selectedMaterialIds, bulkUpdateRemarks, showSnackbar]);

    const handleMarkAsComplete = useCallback(async () => {
        if (!canMarkSelectedAsComplete) return;
        if (!user_id_db) {
            showSnackbar({ message: 'Unable to identify current user', severity: 'error' });
            return;
        }
        if (selectedMaterialIds.length === 0) {
            showSnackbar({ message: 'Please select at least one material', severity: 'warning' });
            return;
        }

        const confirmed: any = await confirm({
            title: 'Confirmation',
            description: buildMaterialCompletionDescription(selectedMaterialIds),
            confirmText: 'Confirm',
            cancelText: 'Cancel',
        });

        if (!confirmed) return;

        setIsManualCompleteSubmitting(true);
        manuallyCompleteMaterials({
            data: {
                material_ids: selectedMaterialIds,
                user_id: Number(user_id_db),
            }
        });
    }, [canMarkSelectedAsComplete, user_id_db, selectedMaterialIds, confirm, manuallyCompleteMaterials, showSnackbar]);

    return {
        selectionModel,
        setSelectionModel,
        openRemarkDialog,
        setOpenRemarkDialog,
        selectedBulkRemark,
        setSelectedBulkRemark,
        bulkRemarkError,
        setBulkRemarkError,
        isBulkRemarkSubmitting,
        isManualCompleteSubmitting,
        selectedMaterialIds,
        canMarkSelectedAsComplete,
        clearBulkSelection,
        handleOpenUpdateRemark,
        handleSaveBulkRemark,
        handleMarkAsComplete,
    };
};
